import {useTheme} from "@emotion/react";
import {Link} from "@/elements/link";
import Markdown from "@/elements/Markdown";
import TitleUnderlinedAnimated from "@/elements/TitleUnderlinedAnimated";
import WebpPicture from "@/elements/WebpPicture";
import type {Card} from "@/generated/api";
import {getCardImageSizes, getDisplayedImage} from "@/utils/cardImage";
import {cardAcf, imageOf, relativeLinkOf} from "@/utils/index";
import {Container, Image, Mask, TextWrapper} from "./styles";

interface PushEntryCardProps {
  cardData?: Card;
  // The image is fetched when the card comes near the screen. Left off in the first section of a page, which may be
  // in the first screen (and so the LCP); on the French home the three cards sit ~7 000 px down.
  lazy?: boolean;
}

const PushEntryCard = ({cardData, lazy}: PushEntryCardProps) => {
  const {breakpoints} = useTheme();
  const {md} = breakpoints;
  const title = cardData?.post_title;
  const text = cardAcf(cardData)?.text;
  const desktopImage = imageOf(cardAcf(cardData)?.desktop_image)?.url;
  const mobileImage = imageOf(cardAcf(cardData)?.mobile_image)?.url;
  // The image shown is the mobile one up to 1024 px (md); its srcset must be the one of that same image
  const shown = getDisplayedImage({isMobile: md, cardData});
  const sizes = getCardImageSizes({
    image: shown,
    isDestinationCard: false,
    layout: "pushEntry",
  });
  const linksData = cardData?.front_links;
  const link = relativeLinkOf(linksData);

  return (
    <Link styles={{whiteSpace: "normal"}} to={link} preventScrollReset={true}>
      <Container>
        <Mask />
        <WebpPicture webp={shown?.webp} sizes={sizes}>
          <Image
            src={md && mobileImage ? mobileImage : desktopImage}
            srcSet={sizes ? shown?.srcset : undefined}
            sizes={sizes}
            alt={title}
            loading={lazy ? "lazy" : undefined}
          />
        </WebpPicture>
        <TextWrapper>
          <TitleUnderlinedAnimated
            underlineHeight={4}
            styles={{
              color: "#FFFFFF",
              fontSize: md ? "1.5rem" : "1.75rem",
              lineHeight: "2rem",
            }}
            wrapperStyles={{marginBottom: 8}}
          >
            {title}
          </TitleUnderlinedAnimated>
          <Markdown styles={{color: "#FFFFFF"}} lineClamp={2}>
            {text}
          </Markdown>
        </TextWrapper>
      </Container>
    </Link>
  );
};
export default PushEntryCard;
